// FILE: src/components/BulkImport.js
//
// Three-step bulk question import workflow.
//
// ── Steps ─────────────────────────────────────────────────────────────────────
//
//   Step 1 — Upload
//     Exam selector (driven by getAllExams() — PMP®, CPMAI™, PMI-ACP®, …)
//     plus a JSON file input.
//     "Preview →" button parses the file and advances to Step 2.
//
//   Step 2 — Preview & Validation
//     Table showing every row with its referenceId, question snippet, and
//     validation status (valid / error / conflict).
//     Summary counts: N valid, N with errors, N conflicts.
//     Answer-key balance line for the valid single-select rows, with a
//     non-blocking warning when any position exceeds 35% on a batch of 20+
//     (see services/answerDistribution.js for the rationale — prod banks
//     accumulated a strong B bias).
//     "Import N valid questions →" button is disabled when N === 0.
//
//   Step 3 — Done
//     Success confirmation.  "Return to Admin" triggers onBack().
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   onBack           function  — return to Admin hub (used on Step 1 and Done)
//   questionStores   object    — keyed by examId (e.g. pmp, cpmai, pmi-acp);
//                                values are Question[] | null.  Used for conflict
//                                detection; null means "not yet loaded" (base
//                                questions are fetched lazily).
//   onImport         function  — onImport(examId, normalizedQuestions[])
//                                called after the user confirms import

import React, { useMemo, useState } from 'react';
import { ensureCertLoaded } from '../../../services/certBankLoader';
import { parseJSON, buildPreview, normalizeImportRow } from '../../../services/importService';
import { evaluateAnswerBalance, formatDistributionLine, BALANCE_MIN_BATCH } from '../../../services/answerDistribution';
import { getAllExams, getExamConfig } from '../../../config/exams';

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#333',
    color: '#fff',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    flex: 1,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #aaa',
    borderRadius: '4px',
    color: '#eee',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  stepIndicator: {
    padding: '12px 24px',
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    fontSize: '0.85rem',
    color: '#999',
    flexShrink: 0,
  },
  stepActive: {
    color: '#1a73e8',
    fontWeight: '700',
  },
  stepSep: {
    color: '#ccc',
  },
  body: {
    flex: 1,
    padding: '40px 32px',
    maxWidth: '840px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  sectionTitle: {
    fontSize: '1.15rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '24px',
    marginTop: 0,
    paddingBottom: '12px',
    borderBottom: '2px solid #eee',
  },
  formGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    fontSize: '0.88rem',
    color: '#444',
    marginBottom: '8px',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    background: '#fff',
    cursor: 'pointer',
    minWidth: '200px',
  },
  fileInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  fileButton: {
    padding: '8px 20px',
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#444',
  },
  fileName: {
    fontSize: '0.88rem',
    color: '#555',
    fontStyle: 'italic',
  },
  errorBox: {
    padding: '10px 16px',
    background: '#fdecea',
    border: '1px solid #ef9a9a',
    borderRadius: '4px',
    color: '#c62828',
    fontSize: '0.88rem',
    marginBottom: '20px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  primaryButton: {
    padding: '10px 28px',
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
  },
  primaryButtonDisabled: {
    padding: '10px 28px',
    background: '#aaa',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'default',
    fontSize: '1rem',
    fontWeight: '600',
  },
  secondaryButton: {
    padding: '10px 28px',
    background: 'transparent',
    border: '1px solid #999',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#555',
  },

  // ── Preview table ────────────────────────────────────────────────────────
  summaryBar: {
    display: 'flex',
    gap: '20px',
    padding: '12px 16px',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    marginBottom: '20px',
    fontSize: '0.9rem',
  },
  summaryItem: (color) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color,
    fontWeight: '600',
  }),
  summaryDot: (color) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  }),
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.88rem',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '24px',
  },
  th: {
    padding: '10px 14px',
    background: '#f5f5f5',
    border: '1px solid #e8e8e8',
    textAlign: 'left',
    fontWeight: '700',
    color: '#555',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  td: {
    padding: '10px 14px',
    border: '1px solid #f0f0f0',
    verticalAlign: 'top',
  },
  statusChip: (status) => {
    const map = {
      valid:    { bg: '#e8f5e9', color: '#2e7d32', label: 'Valid' },
      error:    { bg: '#fdecea', color: '#c62828', label: 'Error' },
      conflict: { bg: '#fff8e1', color: '#e07b00', label: 'Conflict' },
    };
    const s = map[status] || map.error;
    return {
      display: 'inline-block',
      padding: '2px 10px',
      background: s.bg,
      color: s.color,
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '700',
      whiteSpace: 'nowrap',
    };
  },
  refIdMono: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.8rem',
    color: '#666',
  },
  errorList: {
    margin: '4px 0 0 0',
    padding: '0 0 0 16px',
    color: '#c62828',
    fontSize: '0.8rem',
  },
  conflictHint: {
    fontSize: '0.8rem',
    color: '#888',
    marginTop: '4px',
  },

  // ── Done screen ──────────────────────────────────────────────────────────
  successCard: {
    background: '#e8f5e9',
    border: '1px solid #a5d6a7',
    borderRadius: '10px',
    padding: '40px 32px',
    textAlign: 'center',
    maxWidth: '480px',
    margin: '0 auto',
  },
  successIcon: {
    fontSize: '3rem',
    marginBottom: '16px',
  },
  successTitle: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#1b5e20',
    marginBottom: '12px',
  },
  successBody: {
    fontSize: '0.95rem',
    color: '#2e7d32',
    lineHeight: '1.6',
    marginBottom: '28px',
  },

  // ── Confirmation modal ───────────────────────────────────────────────────
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalCard: {
    background: '#fff',
    borderRadius: '10px',
    padding: '28px 32px',
    maxWidth: '460px',
    width: 'calc(100% - 48px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '12px',
  },
  modalBody: {
    fontSize: '0.92rem',
    color: '#444',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  modalExamCallout: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '6px',
    padding: '14px 16px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  modalExamLabel: {
    fontSize: '0.78rem',
    fontWeight: '700',
    color: '#7a5700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  modalExamName: {
    fontSize: '1.5rem',
    fontWeight: '800',
    color: '#333',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
};

// ── Status label map ──────────────────────────────────────────────────────────

const STATUS_LABEL = {
  valid:    'Valid',
  error:    'Error',
  conflict: 'Conflict',
};

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Bulk Import — three-step import workflow.
 *
 * @param {function} onBack               — return to Admin hub
 * @param {object}   questionStores       — { pmp: Question[]|null, cpmai: Question[]|null }
 * @param {function} onImport             — onImport(examId, normalizedQuestions[])
 * @param {function} [onReviewImported]   — optional: jump to Question Editor pre-filtered
 *                                          to Unreviewed + this batch for the just-imported
 *                                          exam. Signature: onReviewImported(examId, batchIds[]).
 */
export default function BulkImport({ onBack, questionStores, onImport, onReviewImported }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'done'

  // ── Step 1 state ────────────────────────────────────────────────────────
  const [selectedExam, setSelectedExam] = useState('pmp');
  const [selectedFile, setSelectedFile] = useState(null);
  const [parseError, setParseError] = useState(null);

  // ── Step 2 state ────────────────────────────────────────────────────────
  const [previewRows, setPreviewRows] = useState([]);
  const [importedCount, setImportedCount] = useState(0); // stored for Step 3 message
  const [importErrors, setImportErrors] = useState([]);  // per-question save errors
  const [isSaving, setIsSaving]         = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false); // exam-type confirmation modal
  // Set of sourceBatchId values actually saved in the most recent import. Used
  // by the "Review imported questions →" hand-off so the Question Editor can
  // scope its list to just this batch. If the uploaded JSON rows already had
  // their own sourceBatchId values those are preserved; rows without one get
  // stamped with a single auto-generated id (`import_<ISO>`) shared by the
  // whole upload so the reviewer sees them as one unit.
  const [importedBatchIds, setImportedBatchIds] = useState([]);

  // ── Step 1 handlers ─────────────────────────────────────────────────────

  function handleFileChange(e) {
    const file = e.target.files[0] || null;
    setSelectedFile(file);
    setParseError(null);
  }

  async function handlePreview() {
    if (!selectedFile) return;
    setParseError(null);

    let text;
    try {
      text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(selectedFile);
      });
    } catch (e) {
      setParseError(`Could not read file: ${e.message}`);
      return;
    }

    const { questions: rawQuestions, error } = parseJSON(text);
    if (error) {
      setParseError(error);
      return;
    }
    if (rawQuestions.length === 0) {
      setParseError('The file contains no questions.');
      return;
    }

    // Use the shared store when loaded (authoritative); otherwise lazily load
    // just this cert's bank for conflict detection (PR 2B — no static loadQuestions).
    let existing;
    if (questionStores[selectedExam] !== null) {
      existing = questionStores[selectedExam];
    } else {
      try {
        existing = (await ensureCertLoaded(selectedExam)).questions;
      } catch (err) {
        setParseError(`Could not load the existing ${selectedExam.toUpperCase()} question bank for conflict detection: ${err?.message || 'unknown error'}.`);
        return;
      }
    }

    const rows = buildPreview(rawQuestions, selectedExam, existing);
    setPreviewRows(rows);
    setStep('preview');
  }

  // ── Step 2 handlers ─────────────────────────────────────────────────────

  function handleBackToUpload() {
    setStep('upload');
    setPreviewRows([]);
    setParseError(null);
  }

  function handleImportClick() {
    // Show exam-type confirmation before committing the save.
    setShowConfirm(true);
  }

  function handleCancelConfirm() {
    setShowConfirm(false);
  }

  async function handleImport() {
    setShowConfirm(false);
    const validRows  = previewRows.filter((r) => r.status === 'valid');
    // Stamp every row without a sourceBatchId with a single auto-generated id
    // shared by the whole upload. Existing sourceBatchId values from the JSON
    // are preserved so content authored with explicit batching (e.g. the
    // content-gap generator) keeps its own grouping.
    const autoBatchId = `import_${new Date().toISOString().replace(/[:.]/g, '-')}`;
    const normalized = validRows.map((r) => {
      const row = normalizeImportRow(r.raw, selectedExam);
      return row.sourceBatchId ? row : { ...row, sourceBatchId: autoBatchId };
    });
    const batchIds = Array.from(
      new Set(normalized.map((q) => q.sourceBatchId).filter(Boolean))
    );
    setIsSaving(true);
    try {
      const result = await onImport(selectedExam, normalized);
      setImportedCount(result?.saved ?? normalized.length);
      setImportErrors(result?.errors ?? []);
      setImportedBatchIds(batchIds);
    } catch (err) {
      setImportedCount(0);
      setImportErrors([err.message]);
      setImportedBatchIds([]);
    } finally {
      setIsSaving(false);
      setStep('done');
    }
  }

  // ── Summary counts ───────────────────────────────────────────────────────

  const validCount    = previewRows.filter((r) => r.status === 'valid').length;
  const errorCount    = previewRows.filter((r) => r.status === 'error').length;
  const conflictCount = previewRows.filter((r) => r.status === 'conflict').length;

  // ── Answer-key balance (valid single-select rows only) ──────────────────
  //
  // Informational check: shows the A/B/C/D key distribution for the rows
  // that will actually be imported, and warns (without blocking) when any
  // position exceeds 35% on a batch of BALANCE_MIN_BATCH+ single-select
  // items. Small batches are shown but never warned on — normal variation.
  const answerBalance = useMemo(
    () => evaluateAnswerBalance(
      previewRows.filter((r) => r.status === 'valid').map((r) => r.raw)
    ),
    [previewRows]
  );

  // ── Step indicator ───────────────────────────────────────────────────────

  function StepIndicator() {
    const steps = ['Upload', 'Preview', 'Done'];
    const currentIdx = step === 'upload' ? 0 : step === 'preview' ? 1 : 2;
    return (
      <div style={styles.stepIndicator}>
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <span style={styles.stepSep}>›</span>}
            <span style={i === currentIdx ? styles.stepActive : {}}>
              {i + 1}. {s}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div data-testid="bulk-import" style={styles.wrapper}>
      {/* Header */}
      <div style={styles.header}>
        <button
          data-testid="bulk-import-back-button"
          style={styles.backButton}
          onClick={step === 'upload' ? onBack : handleBackToUpload}
        >
          ← Back
        </button>
        <span style={styles.headerTitle}>📥 Bulk Import</span>
      </div>

      <StepIndicator />

      <div style={styles.body}>

        {/* ── Step 1: Upload ───────────────────────────────────────────── */}
        {step === 'upload' && (
          <>
            <h2 style={styles.sectionTitle}>Upload Question File</h2>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="bulk-import-exam-select">
                Target Exam
              </label>
              <select
                id="bulk-import-exam-select"
                data-testid="bulk-import-exam-select"
                style={styles.select}
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
              >
                {/* Driven from getAllExams() so new certifications added in
                    src/config/exams.js show up automatically. */}
                {getAllExams().map((exam) => (
                  <option key={exam.examId} value={exam.examId}>
                    {exam.displayName}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Question File (JSON)</label>
              <div style={styles.fileInputWrapper}>
                <label
                  data-testid="bulk-import-file-label"
                  style={styles.fileButton}
                >
                  Choose File
                  <input
                    data-testid="bulk-import-file-input"
                    type="file"
                    accept=".json,application/json"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </label>
                <span data-testid="bulk-import-filename" style={styles.fileName}>
                  {selectedFile ? selectedFile.name : 'No file selected'}
                </span>
              </div>
              <div style={{ marginTop: '10px', fontSize: '0.82rem', color: '#888', lineHeight: '1.6' }}>
                Expected format: JSON array of question objects.{' '}
                Each object must have <code>id</code>, <code>question</code>,{' '}
                <code>choices</code> (array), and <code>correctAnswers</code> (array).<br />
                Optional fields: <code>type</code>, <code>explanation</code>,{' '}
                <code>category</code>, <code>confidence</code>, <code>multiSelect</code>,{' '}
                <code>status</code> (<em>new</em>/active/retired),{' '}
                <code>reviewStatus</code> (<em>unreviewed</em>/reported/in_review/reviewed),{' '}
                <code>sourceBatchId</code>, <code>sourceGenerator</code>.<br />
                Imported questions default to <strong>status: new</strong> and{' '}
                <strong>reviewStatus: unreviewed</strong> when not specified.
              </div>
            </div>

            {parseError && (
              <div data-testid="bulk-import-parse-error" style={styles.errorBox}>
                ⚠ {parseError}
              </div>
            )}

            <div style={styles.actions}>
              <button
                data-testid="bulk-import-preview-button"
                style={selectedFile ? styles.primaryButton : styles.primaryButtonDisabled}
                disabled={!selectedFile}
                onClick={handlePreview}
              >
                Preview →
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Preview & Validation ─────────────────────────────── */}
        {step === 'preview' && (
          <>
            <h2 style={styles.sectionTitle}>
              Preview — {previewRows.length} question{previewRows.length !== 1 ? 's' : ''} found
            </h2>

            {/* Summary bar */}
            <div data-testid="bulk-import-summary" style={styles.summaryBar}>
              <div style={styles.summaryItem('#2e7d32')}>
                <div style={styles.summaryDot('#4caf50')} />
                <span data-testid="bulk-import-valid-count">{validCount} valid</span>
              </div>
              <div style={styles.summaryItem('#c62828')}>
                <div style={styles.summaryDot('#ef5350')} />
                <span data-testid="bulk-import-error-count">{errorCount} error{errorCount !== 1 ? 's' : ''}</span>
              </div>
              <div style={styles.summaryItem('#e07b00')}>
                <div style={styles.summaryDot('#ffb300')} />
                <span data-testid="bulk-import-conflict-count">{conflictCount} conflict{conflictCount !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {conflictCount > 0 && (
              <div style={{ ...styles.errorBox, background: '#fff8e1', border: '1px solid #ffe082', color: '#7a5700', marginBottom: '16px' }}>
                ℹ Conflicting questions already exist in the bank and will be skipped.
                Only valid rows will be imported.
              </div>
            )}

            {/* Answer-key balance — informational, never blocks the import */}
            {answerBalance.total > 0 && (
              <div
                data-testid="bulk-import-answer-balance"
                style={{
                  marginBottom: '16px',
                  padding: '10px 14px',
                  background: '#fafafa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: '#555',
                }}
              >
                <strong style={{ color: '#333' }}>Answer-key balance</strong>
                {' '}({answerBalance.total} single-select):{' '}
                <span data-testid="bulk-import-balance-line">
                  {formatDistributionLine(answerBalance)}
                </span>
                {answerBalance.warnings.length > 0 && (
                  <div
                    data-testid="bulk-import-balance-warning"
                    style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: '#fff8e1',
                      border: '1px solid #ffe082',
                      borderRadius: '4px',
                      color: '#7a5700',
                    }}
                  >
                    ⚠ {answerBalance.warnings
                      .map((w) => `position ${w.position} is the correct answer on ${Math.round(w.share * 100)}% of this batch`)
                      .join('; ')}
                    {' '}— consider redistributing correct answers across positions before importing.
                    Import is not blocked.
                  </div>
                )}
                {!answerBalance.checked && (
                  <span style={{ color: '#999' }}>
                    {' '}(batches under {BALANCE_MIN_BATCH} single-select questions are informational only)
                  </span>
                )}
              </div>
            )}

            {/* Preview table */}
            <table style={styles.table} data-testid="bulk-import-preview-table">
              <thead>
                <tr>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Reference ID</th>
                  <th style={styles.th}>Question</th>
                  <th style={styles.th}>Lifecycle</th>
                  <th style={styles.th}>Validation</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} data-testid="bulk-import-preview-row" data-status={row.status}>
                    <td style={{ ...styles.td, color: '#999', minWidth: '32px' }}>{i + 1}</td>
                    <td style={styles.td}>
                      <span style={styles.refIdMono}>{row.referenceId}</span>
                    </td>
                    <td style={styles.td}>
                      {row.snippet}
                      {row.errors.length > 0 && (
                        <ul style={styles.errorList}>
                          {row.errors.map((err, j) => <li key={j}>{err}</li>)}
                        </ul>
                      )}
                      {row.status === 'conflict' && (
                        <div style={styles.conflictHint}>
                          A question with this id already exists — will be skipped.
                        </div>
                      )}
                    </td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                      {row.status === 'valid' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{
                            fontSize: '0.72rem', fontWeight: '700',
                            padding: '1px 7px', borderRadius: '10px',
                            background: row.importStatus === 'active' ? '#e8f5e9' : '#e3f2fd',
                            color: row.importStatus === 'active' ? '#2e7d32' : '#1565c0',
                          }}>
                            {row.importStatus}
                          </span>
                          <span style={{
                            fontSize: '0.72rem', fontWeight: '600',
                            padding: '1px 7px', borderRadius: '10px',
                            background: '#fff8e1', color: '#e07b00',
                          }}>
                            {row.importReviewStatus}
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statusChip(row.status)}>
                        {STATUS_LABEL[row.status] || row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.actions}>
              <button
                data-testid="bulk-import-import-button"
                style={validCount > 0 && !isSaving ? styles.primaryButton : styles.primaryButtonDisabled}
                disabled={validCount === 0 || isSaving}
                onClick={handleImportClick}
              >
                {isSaving
                  ? `Saving…`
                  : `Import ${validCount} valid question${validCount !== 1 ? 's' : ''} →`}
              </button>
              {!isSaving && (
                <button
                  style={styles.secondaryButton}
                  onClick={handleBackToUpload}
                >
                  ← Back
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Step 3: Done ─────────────────────────────────────────────── */}
        {step === 'done' && (
          <div style={styles.successCard} data-testid="bulk-import-done">
            <div style={styles.successIcon}>{importErrors.length === 0 ? '✅' : '⚠️'}</div>
            <h2 style={styles.successTitle}>
              {importErrors.length === 0 ? 'Import Complete' : 'Import Finished with Errors'}
            </h2>
            <p style={styles.successBody}>
              Successfully saved{' '}
              <strong>{importedCount}</strong>{' '}
              question{importedCount !== 1 ? 's' : ''} into the{' '}
              <strong>{selectedExam.toUpperCase()}</strong> bank.{' '}
              Questions with status <strong>new</strong> will not appear in live exams
              until a reviewer approves them (<em>status → active</em>) via the
              Question Review screen.
            </p>
            {importErrors.length > 0 && (
              <div style={{ ...styles.errorBox, marginBottom: '20px', textAlign: 'left' }}>
                <strong>{importErrors.length} question{importErrors.length !== 1 ? 's' : ''} failed to save:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
                  {importErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {onReviewImported && importedCount > 0 && (
                <button
                  data-testid="bulk-import-review-button"
                  style={styles.primaryButton}
                  onClick={() => onReviewImported(selectedExam, importedBatchIds)}
                >
                  Review imported questions →
                </button>
              )}
              <button
                data-testid="bulk-import-return-button"
                style={
                  onReviewImported && importedCount > 0
                    ? styles.secondaryButton
                    : styles.primaryButton
                }
                onClick={onBack}
              >
                Return to Admin
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ── Exam-type confirmation modal ──────────────────────────────────── */}
      {showConfirm && (
        <div
          data-testid="bulk-import-confirm-overlay"
          style={styles.modalOverlay}
          onClick={handleCancelConfirm}
        >
          <div
            data-testid="bulk-import-confirm-modal"
            style={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>Confirm Target Exam</h3>
            <p style={styles.modalBody}>
              You are about to import{' '}
              <strong>{validCount}</strong>{' '}
              valid question{validCount !== 1 ? 's' : ''} into the following exam bank.
              Please confirm this is correct — imports cannot be undone in bulk.
            </p>
            <div style={styles.modalExamCallout}>
              <div style={styles.modalExamLabel}>Target Exam</div>
              <div
                data-testid="bulk-import-confirm-exam"
                style={styles.modalExamName}
              >
                {getExamConfig(selectedExam)?.displayName ?? selectedExam.toUpperCase()}
              </div>
            </div>
            <div style={styles.modalActions}>
              <button
                data-testid="bulk-import-confirm-cancel"
                style={styles.secondaryButton}
                onClick={handleCancelConfirm}
              >
                Cancel
              </button>
              <button
                data-testid="bulk-import-confirm-ok"
                style={styles.primaryButton}
                onClick={handleImport}
              >
                Yes, Import to {getExamConfig(selectedExam)?.displayName ?? selectedExam.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
